import * as PIXI from "pixi.js";

export interface Loadable<T> {
  get(): T;
}

type LoadableState<T> = Readonly<
  | { status: "pending" }
  | { status: "fulfilled"; value: T }
  | { status: "rejected"; reason: unknown }
>;
class LoadableImpl<T> implements Loadable<T> {
  private state: LoadableState<T>;
  static from<T>(f: () => Promise<T>) {
    return new LoadableImpl(f());
  }
  constructor(promise: Promise<T>) {
    this.state = { status: "pending" };
    promise.then(
      (value) => {
        this.state = {
          status: "fulfilled",
          value,
        };
      },
      (error) => {
        this.state = {
          status: "rejected",
          reason: error,
        };
      },
    );
  }

  get() {
    switch (this.state.status) {
      case "fulfilled":
        return this.state.value;
      case "rejected":
        throw this.state.reason;
      case "pending":
        throw new Error("not resolved");
    }
  }
}

type ResolveStatus<T> = Readonly<
  | {
      status: "fulfilled";
      value: T;
    }
  | {
      status: "rejected";
      reason: unknown;
    }
>;

export class LoadManager {
  private _promises: Promise<ResolveStatus<unknown>>[] = [];

  private addPromise(promise: Promise<unknown>) {
    this._promises.push(
      promise.then(
        (value) => ({ status: "fulfilled", value }),
        (reason) => ({ status: "rejected", reason }),
      ),
    );
  }

  waitForLoad() {
    return Promise.all(this._promises);
  }

  loadTexture(url: string): Loadable<PIXI.Texture> {
    const promise = PIXI.Texture.fromURL(url);
    this.addPromise(promise);
    return new LoadableImpl(promise);
  }
}
